import { getFixturesByEvent } from "core-integration/src/store/fixtures/reducers";
import { createContext, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  fetchBroadcastersForFixtures,
  PulseChannel,
} from "../../../../utils/pulse";
import { PulseBroadcaster } from "../../../../utils/pulse/types";

type BroadcasterInfo = Record<string, Record<string, PulseBroadcaster[]>>;

export const FixtureBroadcastersContext = createContext<BroadcasterInfo>({});

export const normalizeBroadcaster = (
  channelData: PulseChannel,
): PulseBroadcaster | null => {
  if (!channelData) return null;

  // Do not return broadcaster if there is no video type
  const hasVideo = (channelData?.info?.channelTypes ?? []).some(
    ({ mediaType }: { mediaType?: string }) => mediaType === "VIDEO",
  );
  if (!hasVideo) return null;

  // Explicitly check if broadcaster exists and is not null
  const broadcaster = channelData.info?.broadcaster;
  if (!broadcaster) return null;

  // Now safely access properties since we've verified broadcaster is not null
  const abbreviation = broadcaster.abbreviation;
  const name = broadcaster.name;
  const link = broadcaster.link;

  if (!abbreviation || !name || !link) return null;

  return { abbreviation, name, link };
};

const FixtureBroadcasters = ({ children }: { children: ReactNode }) => {
  const [broadcasterInfo, setBroadcasterInfo] = useState<BroadcasterInfo>({});
  const fixturesByEvent = useSelector(getFixturesByEvent);

  useEffect(() => {
    // Limit fetches caused by loading all fixtures for FDR
    let fetchesInProgress = 0;
    Object.keys(fixturesByEvent).forEach(async (event) => {
      // We already have a fetch in progress
      if (fetchesInProgress) return;

      const fixtures = fixturesByEvent[event];
      // There are no fixtures in the event so no broadcasters ...
      if (!fixtures.length) return;

      // We have fixtures but no broadcaster info so fetch it
      if (!broadcasterInfo[event]) {
        fetchesInProgress++;
        const fixtureCodes = fixtures.map((fixture) => fixture.code).join(",");
        const broadcasterData = await fetchBroadcastersForFixtures(
          fixtureCodes,
        ).catch((err) => {
          console.log("Failed to fetch broadcaster data", err);
        });

        if (broadcasterData && broadcasterData.content) {
          const eventBroadcasterInfo: Record<string, PulseBroadcaster[]> = {};

          broadcasterData.content.forEach((data) => {
            const code = data?.contentReference?.id;
            if (!code) return;

            const cData = data?.channels
              ?.map(normalizeBroadcaster)
              .filter((item): item is PulseBroadcaster => item !== null);

            if (cData.length > 0) {
              eventBroadcasterInfo[code] = cData;
            }
          });

          if (Object.keys(eventBroadcasterInfo).length > 0) {
            setBroadcasterInfo((state) => ({
              ...state,
              [event]: eventBroadcasterInfo,
            }));
          }
        }
      }
    });
    // We only want to run this effect when fixturesByEvent changes
  }, [fixturesByEvent]);

  return (
    <FixtureBroadcastersContext.Provider value={broadcasterInfo}>
      {children}
    </FixtureBroadcastersContext.Provider>
  );
};

export default FixtureBroadcasters;
